import React, { Fragment, forwardRef } from 'react';
import PropTypes from 'prop-types';

import MdText from '../../../../MdText';
import { MBox } from '../../../../atoms/MBox';
import { MText } from '../../../../atoms/MText';
import MFlexbox from '../../../../atoms/MFlexbox';
import { MButton } from '../../../../atoms/MButton';

import { ProductImage } from '../../../../atoms/ProductImage';
import PriceDetails from '../../../../molecules/PriceDetails';
import MImage from '../../../../atoms/MImage';

import FBTProduct from './product';

import { StyledFBTEmbeddedWidgetCon } from './skins';

const getCustomMargin = ({ isContainerSizeLarge, isMobile, theme }) => {
  if (isContainerSizeLarge) {
    if (theme.fbtLargeWidthConAlignment === 'right') {
      return theme.widgetMargin
        .split(' ')
        .map((v, i) => (i === 3 ? 'auto' : v))
        .join(' ');
    }
    if (theme.fbtLargeWidthConAlignment === 'left') {
      return theme.widgetMargin
        .split(' ')
        .map((v, i) => (i === 1 ? 'auto' : v))
        .join(' ');
    }
    return theme.widgetMargin
      .split(' ')
      .map((v, i) => (i % 2 !== 0 ? 'auto' : v))
      .join(' ');
  }

  return isMobile ? theme.widgetMobileMargin : theme.widgetMargin;
};

const FBT = forwardRef((props, ref) => {
  const {
    isMobile,
    products,
    productImages,
    displayProducts,
    selectedVariantInfo,
    theme,
    priceDetailStyles,
    isFBTActionLoading,
    isFBTProductsAdded,
    onAddFBTProducts,
    isGlobalDiscountDisabled,
    totalValues,
    containerWidth,
    getProductAndSelectedVariantData,
    ...restProps
  } = props;

  const isContainerSizeLarge = containerWidth > 550;

  const atcButtonWidth = isMobile
    ? theme.productButtonMobileWidth
    : theme.productButtonWidth;

  let atcStatus = theme.fbtAddToCartText;

  if (isFBTActionLoading) atcStatus = theme.atcProductAddingText;
  if (isFBTProductsAdded) atcStatus = theme.atcProductAddedText;

  const isATCInIntermediateState = isFBTActionLoading || isFBTProductsAdded;

  return (
    <StyledFBTEmbeddedWidgetCon
      ref={ref}
      fontFamily={theme.font}
      backgroundColor={theme.offerBackgroundColor}
      border={
        theme.isOfferBordered
          ? `${theme.offerBorderWidth}px solid ${theme.offerBorderColor}`
          : ''
      }
      borderRadius={theme.widgetBorderRadius}
      padding={isMobile ? theme.widgetMobilePadding : theme.widgetPadding}
      margin={getCustomMargin({ isContainerSizeLarge, isMobile, theme })}
      mobilePadding={theme.widgetMobilePadding}
      mobileMargin={getCustomMargin({ isContainerSizeLarge, isMobile, theme })}
      maxWidth={isContainerSizeLarge ? '854px' : '100%'}
      className="mr-embedded-widget mr-embedded-widget--fbt"
    >
      <MdText
        fontSize={`${
          isMobile ? theme.offerHeaderMobileFontSize : theme.offerHeaderFontSize
        }px`}
        color={theme.offerHeaderTextColor}
        alignment={theme.offerHeaderTextAlignment}
        padding={
          isMobile ? theme.offerHeaderMobilePadding : theme.offerHeaderPadding
        }
        fontFamily={theme.offerHeaderFontFamily}
        margin={
          isMobile ? theme.offerHeaderMobileMargin : theme.offerHeaderMargin
        }
        className="mr-embedded-widget__offer-header"
      >
        {theme.offer_header_text}
      </MdText>
      {theme.offerSubtitleText && (
        <MdText
          fontSize={`${
            isMobile
              ? theme.offerSubtitleMobileFontSize
              : theme.offerSubtitleFontSize
          }px`}
          color={theme.offerSubtitleTextColor}
          alignment={theme.offerSubtitleTextAlignment}
          fontFamily={theme.offerSubtitleFontFamily}
          padding={
            isMobile
              ? theme.offerSubtitleMobilePadding
              : theme.offerSubtitlePadding
          }
          margin={
            isMobile
              ? theme.offerSubtitleMobileMargin
              : theme.offerSubtitleMargin
          }
          className="mr-embedded-widget__offer-subtitle"
        >
          {theme.offerSubtitleText}
        </MdText>
      )}
      <MFlexbox
        ai="center"
        flexWrap="wrap"
        className="mr-embedded-widget__fbt-image-container"
        padding={theme.fbtImagesContainerPadding}
        margin={theme.fbtImagesContainerMargin}
        jc={theme.fbtLargeWidthImagesAlignment}
      >
        {/* TODO: prajwal check this, the issue of click happened after image click go to cart feature */}
        {productImages.length > 0 &&
          products.map((product, index) => {
            if (!productImages[index]) {
              return null;
            }

            return (
              <Fragment key={`${productImages[index].src}`}>
                <MFlexbox ai="center" jc="center" className="mr-fbt-image-con">
                  <ProductImage
                    src={productImages[index].src}
                    width={
                      isMobile
                        ? theme.fbtProductImageMobileWidth
                        : theme.fbtProductImageWidth
                    }
                    height={
                      isMobile
                        ? theme.fbtProductImageMobileHeight
                        : theme.fbtProductImageHeight
                    }
                    mobileWidth={theme.fbtProductImageMobileWidth}
                    mobileHeight={theme.fbtProductImageMobileHeight}
                    isBordered={theme.isProductImageBordered}
                    borderColor={theme.productImageBorderColor}
                    borderWidth={theme.productImageBorderWidth}
                    borderRadius={
                      isMobile
                        ? theme.productImageMobileBorderRadius
                        : theme.productImageBorderRadius
                    }
                    padding={theme.productImagePadding}
                    margin={theme.productImageMargin}
                    cursor={
                      theme.isProductImageClickGoToPage ? 'pointer' : 'auto'
                    }
                    onImageClick={
                      getProductAndSelectedVariantData(
                        product,
                        selectedVariantInfo[index] || {},
                      ).onProductImageClick
                    }
                    className="mr-fbt-image-con__image"
                  />
                  {index !== productImages.length - 1 ? (
                    <MImage
                      src="https://resources.monkcommerce.app/app-images/addsign.svg"
                      width="10px"
                      height="22px"
                      className="mr-add-icon"
                      margin={isContainerSizeLarge ? '0 16px' : '0 8px'}
                    />
                  ) : null}
                </MFlexbox>
              </Fragment>
            );
          })}
      </MFlexbox>
      {products.map((product, index) => (
        <Fragment key={`${index.toString()}`}>
          <FBTProduct
            idx={index}
            isMobile={isMobile}
            product={product}
            // As on FBT offer type change a dummy product is added at index 0 and selected variant is undefined before adding dummy product
            selectedVariant={selectedVariantInfo[index] || {}}
            theme={theme}
            priceDetailStyles={priceDetailStyles}
            widgetContainerWidth={containerWidth}
            getProductAndSelectedVariantData={getProductAndSelectedVariantData}
            {...restProps}
          />
        </Fragment>
      ))}
      <MBox className="mr-fbt-action-con">
        <MFlexbox
          gap="4px"
          ai="center"
          flexWrap="wrap"
          className="mr-fbt-total-con"
          padding={theme.fbtTotalAmountPadding}
          margin={theme.fbtTotalAmountMargin}
          jc={theme.fbtTotalAmountTextAlignment}
        >
          <MText
            fontSize={
              isMobile
                ? theme.fbtTotalAmountMobileFontSize
                : theme.fbtTotalAmountFontSize
            }
            mobileFontSize={theme.fbtTotalAmountMobileFontSize}
            color={theme.fbtTotalAmountTextColor}
            fontStyle={theme.fbtTotalAmountFontStyle}
            fontWeight={theme.fbtTotalAmountFontWeight}
            textDecoration={theme.fbtTotalAmountTextDecoration}
            fontFamily={theme.fbtTotalAmountFontFamily}
            className="mr-fbt-total-con__text"
          >
            {theme.fbtTotalAmountText}
          </MText>
          <PriceDetails
            offerPrice={
              isGlobalDiscountDisabled
                ? totalValues.totalPrice
                : totalValues.offerPrice
            }
            price={totalValues.totalPrice}
            off={totalValues.discount}
            isDiscountDisabled={!totalValues.displayDiscount}
            offerPriceStyles={{
              ...priceDetailStyles.offerPriceStyles,
              fontSize: isMobile
                ? theme.fbtTotalAmountMobileFontSize
                : theme.fbtTotalAmountFontSize,
              mobileFontSize: theme.fbtTotalAmountMobileFontSize,
            }}
            priceStyles={{
              ...priceDetailStyles.priceStyles,
              fontSize: isMobile
                ? theme.fbtTotalAmountMobileFontSize
                : theme.fbtTotalAmountFontSize,
              mobileFontSize: theme.fbtTotalAmountMobileFontSize,
            }}
            offStyles={{
              ...priceDetailStyles.offStyles,
              fontSize: isMobile
                ? theme.fbtTotalAmountMobileFontSize
                : theme.fbtTotalAmountFontSize,
              mobileFontSize: theme.fbtTotalAmountMobileFontSize,
            }}
            className="mr-fbt-total-con__price"
          />
        </MFlexbox>
        <MButton
          className="mr-fbt-add-button"
          onClick={onAddFBTProducts}
          buttonStyle={theme.embeddedProductButtonStyle}
          backgroundColor={theme.productButtonColor}
          borderColor={theme.productButtonColor}
          borderRadius={theme.productButtonBorderRadius}
          textColor={theme.embeddedProductButtonTextColor}
          fontSize={
            isMobile
              ? theme.fbtProductButtonMobileFontSize
              : theme.fbtProductButtonFontSize
          }
          mobileFontSize={theme.fbtProductButtonMobileFontSize}
          fontStyle={theme.productButtonFontStyle}
          fontWeight={theme.productButtonFontWeight}
          textDecoration={theme.productButtonTextDecoration}
          textAlignment={theme.productButtonTextAlignment}
          fontFamily={theme.productButtonFontFamily}
          padding={theme.productButtonPadding}
          margin={theme.productButtonMargin}
          height={theme.fbtProductButtonHeight}
          width={
            atcButtonWidth && atcButtonWidth !== '0' ? atcButtonWidth : '100%'
          }
          opacity={isATCInIntermediateState ? '0.7' : '1'}
          disabled={isATCInIntermediateState}
        >
          {atcStatus}
        </MButton>
      </MBox>
    </StyledFBTEmbeddedWidgetCon>
  );
});

FBT.propTypes = {
  isMobile: PropTypes.bool,
  products: PropTypes.array,
  productImages: PropTypes.array,
  displayProducts: PropTypes.array,
  selectedVariantInfo: PropTypes.array,
  theme: PropTypes.object,
  isGlobalDiscountDisabled: PropTypes.bool,
  totalValues: PropTypes.object,
  isFBTActionLoading: PropTypes.bool,
  onAddFBTProducts: PropTypes.func,
  priceDetailStyles: PropTypes.object,
  isFBTProductsAdded: PropTypes.bool,
  containerWidth: PropTypes.number,
  getProductAndSelectedVariantData: PropTypes.func,
};

export default FBT;
